import {
    Box, CloseButton, Drawer, Flex, HStack, Icon, IconButton, Image, LinkBox, LinkOverlay, Menu, Portal, Separator, Spacer, Text
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiBarChart2 } from "react-icons/fi";
import { LuLightbulb, LuSettings2 } from "react-icons/lu";
import { TbCalendar, TbDotsVertical, TbLabel, TbLayoutDashboard, TbMenu2, TbMessage, TbPhoto, TbSpeakerphone, TbUser } from "react-icons/tb";
import { NavLink, useLocation } from 'react-router-dom';
import { Avatar } from './ui/avatar';
import { BetaBadge } from './beta-badge';

import { Links } from '../constants';
import { isCommentsEnabled } from '../helpers/organization';

function SidebarMainLinks({ orgID, organization, onNavClick, ...props }) {
    const location = useLocation()
    let links = []
    function superUserPath(path) {
        if (orgID) {
            return `${path}?orgID=${orgID}`
        }
        return path
    }
    if (location.pathname.startsWith(Links.AdminPagePath)) {
        links = [
            { title: "Content Builder", path: superUserPath(Links.AdminPostsPagePath), icon: TbLayoutDashboard },
            { title: "Calendar", path: superUserPath(Links.AdminCalendarPagePath), icon: TbCalendar },
            { title: "Inspiration Hub", path: superUserPath(Links.AdminInspirationHubPagePath), icon: LuLightbulb },
            { title: "Media", path: superUserPath(Links.AdminMediaPagePath), icon: TbPhoto },
            { title: "Campaigns", path: superUserPath(Links.AdminCampaignsPagePath), icon: TbSpeakerphone },
            isCommentsEnabled(organization) && { title: "Comments", path: superUserPath(Links.AdminCommentsPagePath), icon: TbMessage, beta: true },
            { title: "Analytics", path: superUserPath(Links.AdminAnalyticsPagePath), icon: FiBarChart2 },
            { title: "Users", path: superUserPath(Links.AdminUsersPagePath), icon: TbUser },
            { title: "Channels", path: superUserPath(Links.AdminChannelsPagePath), icon: TbLabel },
            { title: "Settings", path: superUserPath(Links.AdminSettingsPagePath), icon: LuSettings2 },
        ]
    } else {
        links = [
            { title: "Content Gallery", path: Links.AdvocatePostsPagePath, icon: TbLayoutDashboard },
            { title: "Calendar", path: Links.AdvocateCalendarPagePath, icon: TbCalendar },
            isCommentsEnabled(organization) && { title: "Comments", path: Links.AdvocateCommentsPagePath, icon: TbMessage, beta: true },
            { title: "Analytics", path: Links.AdvocateAnalyticsPagePath, icon: FiBarChart2 },
            { title: "Settings", path: Links.AdvocateSettingsPagePath, icon: LuSettings2 },
        ]
    }
    // links of features which are not enabled are false
    links = links.filter(Boolean)
    return (
        <Box as="ul" listStyleType="none" {...props}>
            {links.map(link => (
                <Box as="li" key={link.title}>
                    <NavLink to={link.path}>
                        {({ isActive }) => (
                            <Flex
                                mb="0.5"
                                align="center"
                                p="2"
                                fontSize="sm"
                                fontWeight="normal"
                                transition="background 0.2s ease-in-out"
                                background={isActive ? 'bg.muted' : 'transparent'}
                                color={isActive ? 'fg' : 'fg.muted'}
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={isActive ? "border" : "transparent"}
                                borderStyle="solid"
                                _hover={isActive ? {} : { background: 'bg.muted' }}
                                onClick={onNavClick}
                            >
                                <Icon as={link.icon} marginRight={3} boxSize={5} color={isActive ? "primary.500" : "inherit"} />
                                {link.title}
                                {link.beta && <BetaBadge ms="2" />}
                            </Flex>
                        )}
                    </NavLink>
                </Box>
            ))}
        </Box>
    )
}

function UserLinks({ user, organization, ...props }) {
    const location = useLocation()
    const links = []
    let workspaceName = 'Advocate Dashboard'
    let workspaceToggleLink = "#"
    if (user.UserType.Name === 'admin') {
        if (location.pathname.startsWith(Links.AdminPagePath)) {
            links.unshift({ title: "Advocate Dashboard", path: Links.AdvocatePostsPagePath })
            workspaceName = 'Admin Dashboard'
            workspaceToggleLink = Links.AdvocatePostsPagePath
        } else {
            links.unshift({ title: "Admin Dashboard", path: Links.AdminPostsPagePath })
            workspaceName = 'Advocate Dashboard'
            workspaceToggleLink = Links.AdminPostsPagePath
        }
    }

    const name = user.Name || user.EmailID
    const firstName = name?.split(' ')?.[0]
    return (
        <Box color="gray.500" {...props}>
            <Flex align="center" py={1}>
                <Menu.Root positioning={{ placement: "top-end" }}>
                    <HStack width="100%" gap="0">
                        <LinkBox>
                            <LinkOverlay
                                href={workspaceToggleLink}
                            >
                                <HStack>
                                    <Avatar src={user.AvatarURL} name={user.Name} size="sm" mr={2} />
                                    <Box textAlign="left">
                                        <Text fontSize="sm">{firstName}</Text>
                                        <Text fontSize="xs" color="fg.muted" fontWeight="500">{workspaceName}</Text>
                                    </Box>
                                </HStack>
                            </LinkOverlay>
                        </LinkBox>
                        <Spacer />
                        <Menu.Trigger cursor="pointer" asChild>
                            <IconButton variant="ghost" aria-label="User menu" size="sm">
                                <TbDotsVertical />
                            </IconButton>
                        </Menu.Trigger>
                    </HStack>
                    <Portal>
                        <Menu.Positioner>
                            <Menu.Content>
                                {links.map(link => (
                                    <Menu.Item value={link.title} key={link.title} asChild>
                                        <NavLink to={link.path}>
                                            {link.title}
                                        </NavLink>
                                    </Menu.Item>
                                ))}
                                <Menu.Item value="docs" asChild>
                                    <a href={Links.DocsURL} target="_blank" rel="noopener noreferrer">
                                        Docs
                                    </a>
                                </Menu.Item>
                                <Menu.Item value="logout" asChild>
                                    <a href={Links.logoutPath}>
                                        Sign Out
                                    </a>
                                </Menu.Item>
                            </Menu.Content>
                        </Menu.Positioner>
                    </Portal>
                </Menu.Root>
            </Flex>
        </Box>
    )
}

function SidebarContent({ user, organization, orgID, onClose }) {
    return (
        <Flex fontSize="sm" fontWeight="600" direction="column" h="full">
            <HStack mt="5" mb="4" mx="2">
                {organization?.LogoURL && <Image src={organization?.LogoURL} h={6} borderRadius="xs" />}
                <Text fontSize="md" fontWeight="medium">{organization?.Name}</Text>
            </HStack>
            <SidebarMainLinks orgID={orgID} organization={organization} onNavClick={onClose} />
            <Spacer />
            <Separator mb="sectionGap" borderColor="gray.100" />
            <UserLinks organization={organization} user={user} />
        </Flex >
    )
}

export function MobileNav({ user, organization, orgID }) {
    const [open, setOpen] = useState(false)
    const location = useLocation()

    useEffect(() => {
        setOpen(false)
    }, [location.pathname])

    return (
        <Drawer.Root open={open} onOpenChange={(e) => setOpen(e.open)} placement="start">
            <Flex
                bg="white"
                px="4"
                py="3"
                boxShadow="xs"
                align="center"
                gap="3"
                mb="4"
            >
                <Drawer.Trigger asChild>
                    <IconButton variant="ghost" aria-label="Open menu">
                        <TbMenu2 />
                    </IconButton>
                </Drawer.Trigger>
                <Image src="/logo.svg" h={5} />
            </Flex>
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content>
                        <Drawer.Body p="3">
                            <SidebarContent
                                user={user}
                                organization={organization}
                                orgID={orgID}
                            />
                        </Drawer.Body>
                        <Drawer.CloseTrigger asChild position="absolute" top="2" insetEnd="2">
                            <CloseButton size="sm" />
                        </Drawer.CloseTrigger>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    )
}

export default function Sidebar({ user, organization, orgID, ...props }) {
    return (
        <Flex
            fontSize="sm"
            fontWeight="600"
            direction="column"
            as="nav"
            p="sectionGap"
            bg="white"
            {...props}
        >
            <SidebarContent user={user} organization={organization} orgID={orgID} />
        </Flex>
    )
}
